import { Box, Container, useTheme } from "@mui/material";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import useRunningFitnessProfile from "./useRunningFitnessProfile";
import { t } from "@lingui/macro";
import { useMemo } from "react";
import SnailSection from "../Shared/SnailSection";
import StrengthWeaknessSection from "../Shared/StrengthWeaknessSection";
import KnownTimesSection from "../Shared/KnownTimesSection";
import UpdateAndHistorySection from "../Shared/UpdateAndHistorySection";
import EducationSection from "../Shared/Education/EducationSection";
import { WfIconWorkoutTypesRunningRunning } from "@WahooFitness/redesignr";
import ManualRunningProfileUpdateDialog from "./ManualRunningProfileUpdateDialog";
import { SourcePage } from "@/services/Analytics/EventTypes";
import { WfCardProps } from "@WahooFitness/redesignr/Components/WfCard/WfCard.js";

const RunningProfile = ({ educationCards }: { educationCards: WfCardProps[] }) => {
  const { palette } = useTheme();
  const {
    strength,
    weakness,
    profileTypeName,
    runnerTypeIcon: profileTypeIcon,
    profileDescription,
    dimensions,
    legendItems,
    hasProfile,
    openInfoDrawer,
    isRunningTypeEstimate,
    isRunningTypeManual,
    openEstimateExplanationDrawer,
    runningFitnessProfile,
    openUpdateProfileDrawer,
    editProfileDrawerOpen,
    handleEditDrawerClose,
  } = useRunningFitnessProfile();

  const knownTimes = useMemo(() => {
    return [
      { time: runningFitnessProfile?.dur_1, distance: t`5K` },
      { time: runningFitnessProfile?.dur_2, distance: t`10K` },
      { time: runningFitnessProfile?.dur_3, distance: t`Half Marathon` },
      { time: runningFitnessProfile?.dur_4, distance: t`Marathon` },
    ];
  }, [runningFitnessProfile]);

  const analyticsContext = useMemo(
    () => ({
      name: "athlete_profile_running",
      data: { source_page_name: SourcePage.athlete_profile_running },
    }),
    []
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "100%" }}>
      {runningFitnessProfile && (
        <ManualRunningProfileUpdateDialog
          fitnessProfile={runningFitnessProfile}
          isOpen={editProfileDrawerOpen}
          handleClose={handleEditDrawerClose}
        />
      )}
      <Box
        sx={{
          backgroundColor: palette.background.level1,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          maxHeight: "100%",
          overflow: "auto",
        }}
      >
        <Container
          maxWidth="sm"
          disableGutters
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            pt: 2,
          }}
        >
          <SnailSection
            workoutFamily={WahooWorkoutTypeFamily.Running}
            hasProfile={hasProfile}
            profileTypeName={profileTypeName}
            profileTypeIcon={profileTypeIcon}
            profileDescription={profileDescription}
            dimensions={dimensions}
            legendItems={legendItems}
            openInfoDrawer={openInfoDrawer}
            isProfileEstimate={isRunningTypeEstimate}
            isProfileManual={isRunningTypeManual}
            openEstimateExplanationDrawer={openEstimateExplanationDrawer}
          />
          <StrengthWeaknessSection
            strength={strength}
            weakness={weakness}
            analyticsContext={analyticsContext}
          />
          <KnownTimesSection knownTimes={knownTimes} />
          <UpdateAndHistorySection
            openUpdateProfileDrawer={openUpdateProfileDrawer}
            hasProfile={hasProfile}
            buttonIcon={<WfIconWorkoutTypesRunningRunning fontSize="medium" />}
          />
        </Container>
      </Box>
      <EducationSection educationCards={educationCards} />
    </Box>
  );
};

export default RunningProfile;
